import React, { createContext, useRef } from "react";
import { invokeApi } from "./helpers/authHelper";

export const LookupContext = createContext();

export const LookupProvider = ({ children }) => {
  // using ref instead of state to avoid DOM re-renders until we implement correct context referencing in #23595 - Corey Despres 2026-03-02
  // const [savedLookups, setSavedLookups] = useState({});
  let savedLookups = useRef({});

  // list of keys that LookupProvider uses internally to request fields from API when applicable
  const lookup_gql_keys = {
    investment_objective: {
      inv_objective_cd: null,
      description: null,
      sort_order: null,
      iso_lang_keys: null,
      iso_language: null,
      custodian: null,
    },
    risk_tolerance: {
      fef_risk_tolerance_cd: null,
      sort_order: null,
      iso_lang_keys: null,
      iso_language: null,
      custodian: null,
      weight_score: null,
      description: null,
    },
    security_categories: {
      fef_sec_cat_cd: null,
      iso_lang_keys: null,
      iso_language: null,
    },
    entry_type: {
      iso_language: null,
      iso_lang_keys: null,
      fef_journal_cat_id: null,
      fef_entry_type_id: null,
      fef_entry_class_id: null,
    },
    investment_mix: {
      custodian: null,
      description: null,
      fef_inv_mix_cd: null,
      inv_mix_cd: null,
      iso_lang_keys: null,
      iso_language: null,
      sort_order: null,
    },
    account_type: {
      iso_language: null,
      iso_lang_keys: null,
      is_tax_exempt: null,
      is_rif: null,
      is_registered: null,
      is_locked: null,
      is_lif: null,
      fef_account_type_cd: null,
    },
    fef_cifsc_cds: {
      fef_cifsc_cd: null,
      investment_objectives: null,
    },
    exception_types: {
      exception_type_id: null,
      iso_language: null,
    },
    enhanced_supervision_type: {
      en_sup_type_uuid: null,
      en_sup_type_cd: null,
      iso_language: null,
      sort_order: null,
    },
  };

  const updateLookups = (key, modData) => {
    savedLookups.current = { ...savedLookups.current, [key]: modData };
  };

  const getLookups = async ({ keys, onSuccess = false, onError = false }) => {
    let filtered_keys = keys?.filter(k => !savedLookups.current[k]);
    let currentLookups = JSON.parse(JSON.stringify(savedLookups.current));
    let inError = false;

    // make API request
    if (filtered_keys.length > 0) {
      let body = {
        lookups: {},
        api: "risk7",
      };
      // use GQL keys to build request body
      for (const [key, entry] of Object.entries(lookup_gql_keys)) {
        if (filtered_keys.includes(key)) {
          body.lookups[key] = entry;
        }
      }

      try {
        await invokeApi({
          path: "/app/lookup",
          method: "POST",
          body: body,
          onSuccess: r => {
            // save new lookup fields locally
            currentLookups = { ...currentLookups, ...r?.lookup };
            savedLookups.current = currentLookups;
          },
        });
      } catch (error) {
        if (onError !== false) {
          inError = true;
          onError(error);
        } else {
          throw error;
        }
      }
    }

    // rebuild response object as the API would have returned it
    let response = {
      lookup: {},
    };
    keys.forEach(key => {
      response.lookup[key] = currentLookups[key];
    });

    if (!inError) {
      if (onSuccess !== false) {
        onSuccess(response);
      } else {
        return response;
      }
    }

    return response;
  };

  const contextObj = {
    updateLookups: updateLookups,
    getLookups: getLookups,
  };

  return <LookupContext.Provider value={contextObj}>{children}</LookupContext.Provider>;
};
